import { siteText } from '../siteText'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Film, RotateCcw, X } from 'lucide-react'
import { api, ApiRequestError } from '../api'
import { useAsync } from '../hooks'
import { latestVersion } from '../components/ClipRail'
import type { Clip, Event, Job, User } from '../types'
import { Button, EmptyState, LoadingState, Notice, formatDate, humanize } from '../components/ui'
import { activityClipLocation, type ActivityLocation } from '../activityNavigation'

const INITIAL_UPDATES = 20
type Notification = { id: string; at: string; event?: Event; job?: Job }

export function ActivityView({ onClose, onOpen }: { onClose: () => void; onOpen: (location: ActivityLocation) => void }) {
  const events = useAsync(api.events, [], { pollMs: 10000 })
  const jobs = useAsync(api.jobs, [], { pollMs: 5000 })
  const clips = useAsync(() => api.clips(), [], { pollMs: 10000 })
  const [visibleCount, setVisibleCount] = useState(INITIAL_UPDATES)
  const feedRef = useRef<HTMLDivElement>(null)
  const nextPageRef = useRef<HTMLDivElement>(null)
  const [retryingId, setRetryingId] = useState<string | null>(null)
  const [retryError, setRetryError] = useState<string | null>(null)
  const [openingId, setOpeningId] = useState<string | null>(null)
  const [openError, setOpenError] = useState<string | null>(null)
  const openSequence = useRef(0)
  useEffect(() => () => { openSequence.current += 1 }, [])

  async function openItem(id: string, item: Pick<Event, 'clip_id' | 'source_id'>) {
    if (!item.clip_id && !item.source_id) return
    const sequence = ++openSequence.current
    setOpeningId(id); setOpenError(null)
    try {
      // Resolve the current state instead of trusting an old notification or
      // the cached list. Deleted items keep this feed open with useful copy.
      let location: ActivityLocation
      if (item.clip_id) location = activityClipLocation(await api.clip(item.clip_id))
      else {
        const source = (await api.sourceLibrary()).find(source => source.id === item.source_id)
        if (!source) throw new ApiRequestError('This source is no longer available.', 404)
        location = { route: 'sources', clipId: null, projectId: null, sourceId: source.id }
      }
      if (sequence === openSequence.current) onOpen(location)
    } catch (error) {
      if (sequence === openSequence.current) setOpenError(error instanceof ApiRequestError && [403, 404].includes(error.status)
        ? `This ${item.clip_id ? 'clip' : 'source'} is no longer available. It may have been removed or your access changed.`
        : error instanceof Error ? error.message : 'Could not open this notification. Try again.')
    } finally {
      if (sequence === openSequence.current) setOpeningId(null)
    }
  }

  const updates = useMemo(() => (events.data || []).filter((event) => !(event.kind === 'version_created' && event.message === 'Version 1')), [events.data])
  const clipById = useMemo(() => new Map((clips.data || []).map((clip) => [clip.id, clip])), [clips.data])
  const notifications = useMemo(() => {
    const visibleJobs = (jobs.data || []).filter((job) => job.status === 'failed' || job.status === 'interrupted')
    return [
      ...updates.map((event) => ({ id: `event-${event.id}`, at: event.created_at, event })),
      ...visibleJobs.map((job) => ({ id: `job-${job.id}`, at: job.updated_at, job })),
    ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()) as Notification[]
  }, [jobs.data, updates])
  const groups = groupNotifications(notifications.slice(0, visibleCount))

  useEffect(() => {
    const nextPage = nextPageRef.current
    if (!nextPage || visibleCount >= notifications.length) return
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return
      observer.disconnect()
      setVisibleCount(count => Math.min(count + INITIAL_UPDATES, notifications.length))
    }, { root: feedRef.current, rootMargin: '0px 0px 240px 0px' })
    observer.observe(nextPage)
    return () => observer.disconnect()
  }, [visibleCount, notifications.length])

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [onClose])

  async function retry(id: string) {
    setRetryError(null)
    setRetryingId(id)
    try {
      await api.retryJob(id)
      await Promise.all([jobs.refresh(), events.refresh()])
    } catch (err) {
      setRetryError(err instanceof ApiRequestError ? err.message : 'Could not retry the job.')
    } finally {
      setRetryingId(null)
    }
  }

  return <aside className="activity-page" aria-label="Notifications">
    <div className="activity-feed-panel">
      <header className="activity-feed-header">
        <h1>Notifications</h1>
        <button type="button" className="activity-close" aria-label="Close notifications" onClick={onClose}><X size={22} aria-hidden="true" /></button>
      </header>
      <div ref={feedRef} className="activity-feed-scroll" role="region" aria-label="Notification feed" tabIndex={0}>
      {openError ? <Notice onDismiss={() => setOpenError(null)}>{siteText(openError)}</Notice> : null}
      {retryError ? <div className="activity-notification activity-notification-alert" role="alert"><MotionAvatar /><div className="activity-notification-copy"><p><strong>Retry failed.</strong> {siteText(retryError)}</p></div><button type="button" className="activity-close" aria-label="Dismiss retry error" onClick={() => setRetryError(null)}><X size={16} aria-hidden="true" /></button></div> : null}
      {events.loading && !events.data ? <LoadingState label="Loading notifications" /> : events.error ? <Notice>{siteText(events.error)}</Notice> : groups.length ? groups.map((group) => <section className="activity-feed-group" key={group.label} aria-label={siteText(group.label)}>
        <h2>{siteText(group.label)}</h2>
        {group.items.map((item) => item.event ? <EventNotification key={item.id} event={item.event} clip={item.event.clip_id ? clipById.get(item.event.clip_id) : undefined} clipsLoading={clips.loading && !clips.data} opening={openingId === item.id} onOpen={() => void openItem(item.id, item.event!)} /> : item.job ? <ProcessingRow key={item.id} job={item.job} retrying={retryingId === item.job.id} onRetry={() => void retry(item.job!.id)} opening={openingId === item.id} onOpen={() => void openItem(item.id, item.job!)} /> : null)}
      </section>) : <EmptyState title="No notifications">Team updates and items needing attention will appear here.</EmptyState>}
      {notifications.length > visibleCount ? <div ref={nextPageRef} className="activity-feed-sentinel" aria-hidden="true" /> : null}
      </div>
    </div>
  </aside>
}

function EventNotification({ event, clip, clipsLoading, opening, onOpen }: { event: Event; clip?: Clip; clipsLoading: boolean; opening: boolean; onOpen: () => void }) {
  const actor = event.actor?.name || 'Motion'
  const retryQueued = event.kind === 'job_retry_queued'
  const title = retryQueued ? jobLabel(event.message).toLowerCase() : clip?.title || (event.kind === 'clip_created' ? event.message : 'a clip')
  const version = latestVersion(clip)
  const thumbnail = version?.thumbnail_url || (version?.preview_url ? `/api/versions/${encodeURIComponent(version.id)}/thumbnail/poster?policy=person-v1` : null)
  const hasClip = Boolean(event.clip_id)
  const detail = ['revision_requested', 'changes_requested', 'clip_rejected', 'clip_released'].includes(event.kind) ? event.message : null
  const hasTarget = Boolean(event.clip_id || event.source_id)
  const content = <>
    <Avatar user={event.actor} />
    <div className="activity-notification-copy"><p><strong>{siteText(actor)}</strong> {siteText(retryQueued ? 'queued' : eventAction(event.kind))} <strong>{siteText(title)}</strong>{siteText(retryQueued ? ' for retry' : event.kind === 'clip_approved' ? ' for editing' : '')}. <time dateTime={event.created_at} title={siteText(formatDate(event.created_at, true))}>{siteText(relativeTime(event.created_at))}</time></p>{detail ? <span className="activity-notification-detail">{siteText(detail)}</span> : null}</div>
    {hasClip ? <NotificationThumbnail key={thumbnail || 'no-thumbnail'} src={thumbnail} loading={clipsLoading || clip?.status === 'rendering'} /> : null}
  </>
  return hasTarget ? <button type="button" className={`activity-notification activity-notification-open ${hasClip ? '' : 'activity-notification-no-thumb'}`} aria-label={`Open ${hasClip ? clip?.title || 'clip' : 'source'}`} disabled={opening} aria-busy={opening} onClick={onOpen}>{content}</button>
    : <div className="activity-notification activity-notification-no-thumb">{content}</div>
}

function NotificationThumbnail({ src, loading }: { src: string | null; loading: boolean }) {
  const [ready, setReady] = useState(false)
  const [failed, setFailed] = useState(false)
  const pending = !failed && (src ? !ready : loading)
  return <span className={`activity-notification-thumb ${pending ? 'activity-notification-thumb-loading' : ''} ${!src || failed ? 'activity-notification-thumb-empty' : ''}`} aria-label={pending ? 'Loading clip preview' : !src || failed ? 'Clip preview unavailable' : undefined}>
    {src && !failed ? <img src={src} alt="" loading="lazy" className={ready ? 'is-ready' : ''} onLoad={() => setReady(true)} onError={() => setFailed(true)} /> : null}
    {pending ? <span className="motion-logo activity-notification-thumb-logo" aria-hidden="true" /> : !src || failed ? <Film size={15} aria-hidden="true" /> : null}
  </span>
}

function ProcessingRow({ job, retrying, onRetry, opening, onOpen }: { job: Job; retrying: boolean; onRetry: () => void; opening: boolean; onOpen: () => void }) {
  const failed = job.status === 'failed' || job.status === 'interrupted'
  const canRetry = isActivityJobRetryable(job)
  const detail = job.kind === 'capture' && !canRetry
    ? `${jobErrorMessage(job)} Reconnect from the source workspace.`
    : jobErrorMessage(job)
  return <div className={`activity-notification ${failed ? 'activity-notification-alert' : ''}`}>
    <MotionAvatar />
    <div className="activity-notification-copy"><p><strong>{siteText(failed ? jobLabel(job.kind) : 'Motion')}</strong> {siteText(failed ? 'needs attention.' : job.status === 'complete' ? `completed ${processingLabel(job.kind)}.` : job.status === 'running' ? `is working on ${processingLabel(job.kind)}.` : `queued ${processingLabel(job.kind)}.`)} <time dateTime={job.updated_at} title={siteText(formatDate(job.updated_at, true))}>{siteText(relativeTime(job.updated_at))}</time></p>{failed ? <span className="activity-notification-detail">{siteText(detail)}</span> : job.message && !['complete', 'needs attention'].includes(job.message.toLowerCase()) ? <span className="activity-notification-detail">{siteText(job.message)}</span> : null}{job.clip_id || job.source_id ? <button type="button" className="activity-job-open" disabled={opening} aria-busy={opening} onClick={onOpen}>{job.clip_id ? 'Open clip' : 'Open source'}</button> : null}</div>
    {failed && canRetry ? <Button variant="secondary" size="sm" busy={retrying} onClick={onRetry}><RotateCcw size={13} /> Retry</Button> : null}
  </div>
}

const JOB_ERROR_COPY: Record<string, string> = {
  capture_owner_lost: 'The Mac capture lost its owner before it could finish.',
  capture_failed: 'The Mac capture stopped before it could finish.',
  desktop_capture_failed: 'The Mac capture stopped before it could finish.',
  decode_error: 'Motion could not decode the source. Check the source and try again.',
  configuration_error: 'Motion could not use this configuration. Check the source and try again.',
  job_ended: 'The processing task ended before it could finish.',
  lease_expired: 'The capture session expired before the Mac reconnected.',
  owner_revoked: 'The capture session lost its Mac owner.',
  provider_rate_limited: 'The AI service is busy. Try again shortly.',
  provider_error: 'The AI service could not finish this work. Try again shortly.',
  rate_limit_exceeded: 'The AI service is busy. Try again shortly.',
  response_error: 'The AI service returned an unusable response. Try again shortly.',
  service_error: 'The service could not finish this work. Try again shortly.',
  source_deleted: 'The source was deleted before this work finished.',
  temporary_failure: 'The service could not finish this work. Try again shortly.',
  timeout: 'The service took too long to finish this work. Try again shortly.',
}

function parseJobError(value: string) {
  const normalized = value.trim().toLowerCase()
  if (JOB_ERROR_COPY[normalized]) return { code: normalized }
  // A few worker paths persist a small JSON error envelope. Preserve the
  // human message when present, but still translate its machine code.
  if (value.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(value) as { code?: unknown; message?: unknown; error?: unknown }
      if (typeof parsed.code === 'string') {
        const code = parsed.code.trim().toLowerCase()
        if (JOB_ERROR_COPY[code]) return { code }
        return { opaque: true }
      }
      if (typeof parsed.error === 'string' && parsed.error.trim()) return { message: parsed.error.trim() }
      if (typeof parsed.message === 'string' && parsed.message.trim()) return { message: parsed.message.trim() }
      return { opaque: true }
    } catch { /* fall through to the generic copy below */ }
  }
  return null
}

export function jobErrorMessage(job: Pick<Job, 'kind' | 'error' | 'message'>) {
  const raw = String(job.error || job.message || '').trim()
  if (!raw) return 'The job did not finish.'
  const parsed = parseJobError(raw)
  if (parsed?.code) return JOB_ERROR_COPY[parsed.code]
  if (parsed?.message) return parsed.message
  if (parsed?.opaque) return 'Motion could not finish this work. Check the source and try again.'
  // Never put an opaque snake_case error token in a producer-facing feed.
  if (/^[a-z][a-z0-9]*(?:[_-][a-z0-9]+)+$/.test(raw)) return 'Motion could not finish this work. Check the source and try again.'
  return raw
}

export function isActivityJobRetryable(job: Pick<Job, 'status' | 'can_retry'>) {
  // The server checks the session's role, job owner and desktop capture lease.
  // Missing capabilities fail closed, including during mixed-version updates.
  return (job.status === 'failed' || job.status === 'interrupted') && job.can_retry === true
}

function Avatar({ user }: { user: User | null }) {
  if (!user) return <MotionAvatar />
  if (user.email.toLowerCase() === 'will@willweinbach.com') return <div className="activity-avatar" aria-hidden="true"><img src="/avatars/will.png" alt="" /></div>
  return <div className="activity-avatar" aria-hidden="true">{siteText(user.name.split(/\s+/).map((part) => part[0]).slice(0, 2).join('').toUpperCase())}</div>
}

function MotionAvatar() {
  return <div className="activity-avatar activity-avatar-motion" aria-hidden="true"><img src="/motion-app-icon.png" alt="" /></div>
}

function eventAction(kind: string) {
  return ({ clip_created: 'found a new clip:', version_created: 'created a new cut of', clip_approved: 'approved', clip_rejected: 'rejected', revision_requested: 'requested an AI revision of', clip_claimed: 'started editing', clip_released: 'released', final_returned: 'returned a final cut of', final_approved: 'approved the final cut of', changes_requested: 'requested changes to', premiere_delivery_created: 'prepared a Premiere package for' } as Record<string, string>)[kind] || 'updated'
}

function jobLabel(kind: string) {
  return ({ analyze: 'Source analysis', capture: 'Stream capture', live_segment: 'Live segment', render: 'Clip preview', revise: 'Clip revision' } as Record<string, string>)[kind] || humanize(kind)
}

function processingLabel(kind: string) {
  return ({ analyze: 'source analysis', capture: 'a stream capture', live_segment: 'a live segment', render: 'a clip preview', revise: 'a clip revision' } as Record<string, string>)[kind] || humanize(kind).toLowerCase()
}

function relativeTime(value: string) {
  const elapsed = Date.now() - new Date(value).getTime()
  if (!Number.isFinite(elapsed) || elapsed < 0) return formatDate(value, true)
  const minutes = Math.floor(elapsed / 60000)
  if (minutes < 1) return 'now'
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  return `${days}d`
}

function groupNotifications(items: Notification[]) {
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  const weekStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7)
  const monthStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30)
  const groups = ['Today', 'Yesterday', 'This week', 'This month', 'Earlier'].map((label) => ({ label, items: [] as Notification[] }))
  for (const item of items) {
    const date = new Date(item.at)
    const day = date.toDateString()
    const group = day === today.toDateString() ? groups[0] : day === yesterday.toDateString() ? groups[1] : date >= weekStart ? groups[2] : date >= monthStart ? groups[3] : groups[4]
    group.items.push(item)
  }
  return groups.filter((group) => group.items.length)
}
